/**
 * 계정 **얼굴·팔로워를 그 자리에서 채우는 창구** — 수집 백엔드 `POST /api/profiles` 의 계약.
 *
 * 발굴 색인에 **없는 계정**도 채운다. 화면에 뜬 계정을 묻고, 없으면 백엔드가 뒤에서 받아 채운다.
 * 계약서 정본은 수집 백엔드(ops-auto-backend) 레포의 `docs/PROFILE-API.md` 다.
 *
 * ```
 *   보낸다  { items: [{ channel, account }] }        최대 20건
 *   받는다  { results: [{ channel, account, status, profilePicS3Url, followerCount, … }] }
 * ```
 * 🚨 **순서가 요청과 다를 수 있다** — `channel + account` 로 맞춘다.
 * 🚨 HTTP 는 실패해도 **늘 200** 이다(실패는 `status` 로 온다) — 500 은 백엔드가 고장 났을 때뿐이다.
 * ⚠️ **죽은 옛 세대 미러는 `null` 로 온다** — 주소가 있어도 403 이라 그리면 깨진다(백엔드가 가린다).
 * ⚠️ 비용이 든다 — 하루 새로 받는 계정에 **전체 상한**이 있다(넘으면 `deferred`). 화면에 보이는 것만 묻는다.
 */

/** 창구가 아는 채널 — 색인의 `source` 값을 그대로 싣는다 · 그 밖은 `unsupported` 로 돌아온다 */
export const PROFILE_LOOKUP_CHANNELS = ['instagram', 'tiktok', 'youtube', 'x', 'threads'] as const;
export type ProfileLookupChannel = (typeof PROFILE_LOOKUP_CHANNELS)[number];

/** 한 번에 묻는 최대 건수 — 백엔드 `maxItems` 와 같다 */
export const PROFILE_LOOKUP_BATCH_MAX = 20;

/** 다시 묻는 사이 · 몇 번까지 — 실측 수집 지연은 틱톡 1.7초 · 유튜브 2.0초 · 인스타 8.1초다 */
export const PROFILE_LOOKUP_POLL_MS = 3000;
export const PROFILE_LOOKUP_POLL_MAX = 6;

export type ProfileLookupItem = { channel: ProfileLookupChannel; account: string };

/**
 * `ready` 그린다 · `pending` 다시 묻는다 · `unavailable` 못 받았다(6시간 뒤 자동 재시도) ·
 * `unsupported` 어댑터 없는 채널(영구) · `deferred` 오늘 상한을 넘었다.
 * 🔑 **뒤 셋은 그만 묻으라는 신호**다.
 */
export type ProfileLookupStatus = 'ready' | 'pending' | 'unavailable' | 'unsupported' | 'deferred';

/** ⚠️ `pending`·`unavailable`·`deferred` 에도 값이 실려 올 수 있다(색인에 팔로워만 있는 경우) */
export type ProfileLookupResult = {
  channel: string;
  account: string;
  status: ProfileLookupStatus;
  /** 얼굴 주소 — 없으면 빈 문자열(부르는 쪽이 머리글자로 떨어뜨린다) */
  profilePicS3Url: string;
  /** 팔로워 — 유튜브는 구독자를 이 한 칸으로 합쳐 온다 · 모르면 `null` */
  followerCount: number | null;
  /** 프로필에 적힌 이름(핸들이 아니다) — 없으면 빈 문자열 */
  userName: string;
  isVerified: boolean;
  /** `channel`(지원 안 함) · `dailyCap` · 수집 실패 사유 · 없으면 빈 문자열 */
  reason: string;
};
